import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Star, 
  Trophy, 
  Users, 
  MessageCircle, 
  Heart, 
  Calendar,
  Edit,
  Settings,
  Share,
  Bitcoin,
  TrendingUp,
  Award,
  Badge
} from 'lucide-react'
import { useStore } from '../store/useStore'

const Profile = () => {
  const { user, walletConnected } = useStore()
  const [activeTab, setActiveTab] = useState('nfts')

  // Mock user data
  const userProfile = {
    username: 'CryptoLunatic',
    avatar: '/api/placeholder/150/150',
    bio: 'Passionate about Bitcoin Ordinals and the Lunatics community. Diamond hands since day one! 🚀',
    twitterHandle: '@cryptolunatic',
    joinDate: 'March 2024',
    level: 'Lunatic Elite',
    walletAddress: 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
    stats: {
      socialScore: 8500,
      rank: 1,
      totalEarned: 2.47,
      followers: 1247,
      following: 342,
      nftsOwned: 15,
      tradingVolume: 12.34
    }
  }

  const ownedNFTs = [
    {
      id: '1',
      name: 'Lunatic #1247',
      image: '/api/placeholder/300/300',
      rarity: 'Legendary',
      purchasePrice: 0.0125,
      currentPrice: 0.0155,
      change: 24.0
    },
    {
      id: '2',
      name: 'Lunatic #892',
      image: '/api/placeholder/300/300',
      rarity: 'Epic',
      purchasePrice: 0.0167,
      currentPrice: 0.0189,
      change: 13.2
    },
    {
      id: '3',
      name: 'Lunatic #3456',
      image: '/api/placeholder/300/300',
      rarity: 'Rare',
      purchasePrice: 0.0134,
      currentPrice: 0.0142,
      change: 6.0
    }
  ]

  const achievements = [
    {
      id: '1',
      name: 'First Lunatic',
      description: 'Purchased your first Lunatic NFT',
      icon: Star,
      earned: true,
      rarity: 'common'
    },
    {
      id: '2',
      name: 'Community Leader',
      description: 'Reached top 10 on the leaderboard',
      icon: Trophy,
      earned: true,
      rarity: 'rare'
    },
    {
      id: '3',
      name: 'Diamond Hands',
      description: 'Held NFTs for 6+ months',
      icon: Badge,
      earned: true,
      rarity: 'epic'
    },
    {
      id: '4',
      name: 'Whale Status',
      description: 'Own 50+ Lunatic NFTs',
      icon: Award,
      earned: false,
      rarity: 'legendary'
    }
  ]

  const recentActivity = [
    {
      id: '1',
      type: 'purchase',
      description: 'Purchased Lunatic #1247',
      amount: 0.0155,
      time: '2 hours ago'
    },
    {
      id: '2',
      type: 'social',
      description: 'Reached #1 on leaderboard',
      time: '1 day ago'
    },
    {
      id: '3',
      type: 'achievement',
      description: 'Unlocked "Community Leader" achievement',
      time: '3 days ago'
    }
  ]

  if (!walletConnected) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Users className="w-16 h-16 text-dark-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Connect Your Wallet</h2>
          <p className="text-dark-400 mb-6">Connect your wallet to view your profile</p>
          <button className="btn-primary">Connect Wallet</button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Profile Header */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-shrink-0">
            <img
              src={userProfile.avatar}
              alt={userProfile.username}
              className="w-32 h-32 rounded-full border-4 border-lunatic-500 mx-auto md:mx-0"
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white">{userProfile.username}</h1>
                <p className="text-lunatic-400">{userProfile.twitterHandle}</p>
                <p className="text-sm text-dark-400 mt-1">Member since {userProfile.joinDate}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button className="btn-secondary">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </button>
                <button className="btn-secondary">
                  <Share className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-dark-300 mb-4">{userProfile.bio}</p>
            <div className="flex items-center space-x-6">
              <div className="text-center">
                <p className="text-xl font-bold text-white">{userProfile.stats.followers}</p>
                <p className="text-sm text-dark-400">Followers</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-white">{userProfile.stats.following}</p>
                <p className="text-sm text-dark-400">Following</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-lunatic-400">#{userProfile.stats.rank}</p>
                <p className="text-sm text-dark-400">Rank</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-bitcoin-400">₿ {userProfile.stats.totalEarned}</p>
                <p className="text-sm text-dark-400">Total Earned</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: 'Social Score', value: userProfile.stats.socialScore.toLocaleString(), icon: MessageCircle, color: 'text-lunatic-400' },
          { title: 'NFTs Owned', value: userProfile.stats.nftsOwned.toString(), icon: Star, color: 'text-bitcoin-400' },
          { title: 'Trading Volume', value: `₿ ${userProfile.stats.tradingVolume}`, icon: TrendingUp, color: 'text-green-400' },
          { title: 'Level', value: userProfile.level, icon: Trophy, color: 'text-yellow-400' }
        ].map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-dark-400">{stat.title}</p>
                <p className="text-xl font-bold text-white mt-1">{stat.value}</p>
              </div>
              <div className="p-3 rounded-full bg-dark-800/50">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex space-x-2">
        {['nfts', 'activity', 'achievements'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === tab
                ? 'bg-lunatic-500 text-white'
                : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* NFTs Tab */}
      {activeTab === 'nfts' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ownedNFTs.map((nft, index) => (
            <motion.div
              key={nft.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card card-hover"
            >
              <div className="relative">
                <img
                  src={nft.image}
                  alt={nft.name}
                  className="w-full h-64 object-cover rounded-lg mb-4"
                />
                <div className="absolute top-2 right-2 bg-dark-900/80 backdrop-blur-sm rounded-full px-2 py-1">
                  <span className={`text-xs font-semibold ${
                    nft.rarity === 'Legendary' ? 'text-yellow-400' :
                    nft.rarity === 'Epic' ? 'text-purple-400' :
                    nft.rarity === 'Rare' ? 'text-blue-400' :
                    'text-gray-400'
                  }`}>
                    {nft.rarity}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-white">{nft.name}</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-dark-400">Purchase Price</p>
                    <p className="text-sm font-semibold text-white">₿ {nft.purchasePrice.toFixed(4)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-dark-400">Current Value</p>
                    <p className="text-sm font-semibold text-bitcoin-400">₿ {nft.currentPrice.toFixed(4)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-semibold ${
                    nft.change >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {nft.change >= 0 ? '+' : ''}{nft.change.toFixed(1)}% P&L
                  </span>
                  <button className="btn-secondary text-sm px-4 py-2">
                    List for Sale
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-4 p-4 bg-dark-800/50 rounded-lg"
              >
                <div className={`p-3 rounded-full ${
                  activity.type === 'purchase' ? 'bg-bitcoin-500/20' :
                  activity.type === 'social' ? 'bg-lunatic-500/20' :
                  'bg-green-500/20'
                }`}>
                  {activity.type === 'purchase' ? (
                    <Bitcoin className="w-5 h-5 text-bitcoin-400" />
                  ) : activity.type === 'social' ? (
                    <TrendingUp className="w-5 h-5 text-lunatic-400" />
                  ) : (
                    <Award className="w-5 h-5 text-green-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-white">{activity.description}</p>
                  <p className="text-sm text-dark-400">{activity.time}</p>
                </div>
                {activity.amount && (
                  <div className="text-right">
                    <p className="text-sm font-semibold text-bitcoin-400">₿ {activity.amount.toFixed(4)}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Achievements Tab */}
      {activeTab === 'achievements' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {achievements.map((achievement, index) => (
            <motion.div
              key={achievement.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`card ${achievement.earned ? 'border-2 border-lunatic-500/30' : 'opacity-50'}`}
            >
              <div className="flex items-center space-x-4">
                <div className={`p-4 rounded-full ${
                  achievement.rarity === 'legendary' ? 'bg-yellow-500/20' :
                  achievement.rarity === 'epic' ? 'bg-purple-500/20' :
                  achievement.rarity === 'rare' ? 'bg-blue-500/20' :
                  'bg-gray-500/20'
                }`}>
                  <achievement.icon className={`w-8 h-8 ${
                    achievement.rarity === 'legendary' ? 'text-yellow-400' :
                    achievement.rarity === 'epic' ? 'text-purple-400' :
                    achievement.rarity === 'rare' ? 'text-blue-400' :
                    'text-gray-400'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">{achievement.name}</h3>
                  <p className="text-sm text-dark-400">{achievement.description}</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      achievement.rarity === 'legendary' ? 'bg-yellow-500/20 text-yellow-400' :
                      achievement.rarity === 'epic' ? 'bg-purple-500/20 text-purple-400' :
                      achievement.rarity === 'rare' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {achievement.rarity}
                    </span>
                    {achievement.earned && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                        Earned
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Profile 